import React from 'react'
import { Search, Loader2 } from 'lucide-react'
import { TONE } from '../lib/support'

// Support Console primitives. Styling follows design_handoff_admin_support_console/README.md;
// the shared .btn/.card/.tag/.data-table classes live in index.css.

export const Dot = ({ tone = 'muted', size = 8, className = '' }) => (
  <span
    aria-hidden="true"
    className={`inline-block flex-none rounded-full ${TONE[tone]?.dot || TONE.muted.dot} ${className}`}
    style={{ width: size, height: size }}
  />
)

export const Tag = ({ tone = 'muted', children, className = '' }) => (
  <span className={`tag ${TONE[tone]?.tag || TONE.muted.tag} ${className}`}>{children}</span>
)

/** Status dot that always carries a text label — never colour alone. */
export const StatusLabel = ({ tone, children, size = 8, className = '' }) => (
  <span className={`inline-flex items-center gap-1.5 text-[13px] ${className}`}>
    <Dot tone={tone} size={size} />
    {children}
  </span>
)

export const PageHeader = ({ title, subtitle, actions }) => (
  <div className="flex flex-wrap items-end gap-3">
    <div className="min-w-[240px] flex-1">
      <h1 className="mb-0.5 text-[22px]">{title}</h1>
      {subtitle && <p className="text-[13px] text-admin-600">{subtitle}</p>}
    </div>
    {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
  </div>
)

export const SectionCard = ({ title, aside, children, className = '', bodyClassName = 'gap-2' }) => (
  <section className={`card flex flex-col p-4 ${bodyClassName} ${className}`}>
    {(title || aside) && (
      <div className="flex flex-wrap items-center gap-2">
        {title && <h4 className="flex-1 text-sm">{title}</h4>}
        {aside}
      </div>
    )}
    {children}
  </section>
)

export const SearchInput = ({ value, onChange, placeholder, className = '', inputClassName = '', ...rest }) => (
  <div className={`relative ${className}`}>
    <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-admin-500" />
    <input
      type="search"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className={`input pl-[38px] ${inputClassName}`}
      {...rest}
    />
  </div>
)

export const FilterChip = ({ selected, onClick, children, count, size = 'md' }) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={selected}
    className={`flex items-center gap-1.5 rounded-md border transition-colors ${
      size === 'sm' ? 'px-3 py-1 text-xs' : 'px-3.5 py-1.5 text-[13px]'
    } ${
      selected
        ? 'border-admin-900 bg-admin-900 text-white'
        : 'border-admin-200 bg-transparent text-admin-900 hover:bg-admin-100'
    }`}
  >
    {children}
    {count !== undefined && <span className="text-[11px] opacity-75">{count}</span>}
  </button>
)

export const Segmented = ({ options, value, onChange, className = '', label }) => (
  <div
    role="group"
    aria-label={label}
    className={`flex overflow-hidden rounded-md border border-admin-200 ${className}`}
  >
    {options.map((o) => (
      <button
        key={o.value}
        type="button"
        aria-pressed={value === o.value}
        onClick={() => onChange(o.value)}
        className={`flex-1 px-1.5 py-1.5 text-[13px] transition-colors ${
          value === o.value ? 'bg-primary-600 text-white' : 'bg-transparent text-admin-900 hover:bg-admin-50'
        }`}
      >
        {o.label}
      </button>
    ))}
  </div>
)

export const ProgressBar = ({ tone = 'ok', width = '0%', height = 6 }) => (
  <span className="block overflow-hidden rounded-md bg-admin-200" style={{ height }}>
    <span className={`block ${TONE[tone].dot}`} style={{ height, width }} />
  </span>
)

export const Fact = ({ label, children }) => (
  <div className="flex flex-col leading-snug">
    <span className="text-[11px] text-admin-500">{label}</span>
    <span className="break-all text-[13px]">{children ?? '—'}</span>
  </div>
)

export const KeyValueRow = ({ label, children }) => (
  <div className="flex justify-between gap-3 border-b border-admin-200 pb-2 text-[13px]">
    <span className="text-admin-600">{label}</span>
    <span className="text-right font-semibold">{children}</span>
  </div>
)

export const Spinner = ({ className = 'py-10' }) => (
  <div className={`flex items-center justify-center ${className}`}>
    <Loader2 className="h-5 w-5 animate-spin text-admin-400" />
  </div>
)

export const ErrorBanner = ({ children }) => (
  <div role="alert" className="rounded-md border border-red-200 bg-red-50 px-3 py-2.5 text-[13px] text-red-800">
    {children}
  </div>
)

/** Shown where a section depends on support-console backend work that isn't deployed. */
export const NotAvailable = ({ children }) => (
  <p className="rounded-md bg-admin-50 px-3 py-2.5 text-[13px] text-admin-600">{children}</p>
)

export const Avatar = ({ children, size = 34, className = 'bg-primary-100 text-primary-800' }) => (
  <span
    className={`grid flex-none place-items-center rounded-full text-xs font-bold ${className}`}
    style={{ width: size, height: size }}
  >
    {children}
  </span>
)
